<template>
  <div class="flex flex-col gap-4 px-4 sm:px-6 lg:px-8 py-6 w-full">
    <!-- Custom page-header content (rendered above the optional nav + main
         columns). Used by StandardToolPage to render its rich header full
         width while still letting pages add a section nav beside the body. -->
    <slot name="header" />

    <!-- Default header (auto-rendered from `title` / `subtitle` props unless
         a custom header is provided or `hideDefaultHeader` is set). -->
    <header
      v-if="!hideDefaultHeader && !$slots.header"
      class="flex flex-col gap-3"
    >
      <AppBreadcrumbs :items="resolvedBreadcrumbs" />

      <div class="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4">
        <div class="min-w-0 flex-1">
          <h1 class="text-xl font-semibold text-theme-text [overflow-wrap:anywhere]">
            {{ title }}
          </h1>
          <p v-if="subtitle" class="text-sm text-theme-text-secondary">
            {{ subtitle }}
          </p>
        </div>

        <div v-if="$slots.actions" class="flex-shrink-0 flex flex-wrap items-center gap-2">
          <slot name="actions" />
        </div>
      </div>
    </header>

    <!-- Body: optional left section-nav, content area. -->
    <div :class="['flex-1 min-h-0 flex flex-col lg:flex-row gap-4', $slots.nav ? '' : 'lg:flex-col']">
      <aside
        v-if="$slots.nav"
        class="lg:w-56 lg:flex-shrink-0"
      >
        <nav
          class="rounded-theme-md border bg-theme-surface border-theme-border p-1
            dark:bg-theme-surface dark:border-theme-border lg:sticky lg:top-[80px]"
        >
          <slot name="nav" />
        </nav>
      </aside>

      <main class="flex-1 min-w-0">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import AppBreadcrumbs from '@/components/ui/nav/AppBreadcrumbs.vue';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';

  const props = defineProps({
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    hideDefaultHeader: { type: Boolean, default: false },
    breadcrumbs: { type: [Array, Boolean, Function], default: undefined },
  });

  const route = useRoute() || {};
  const { t } = useI18n();

  function toPath(to) {
    if (!to) return '';
    if (typeof to === 'string') return to.split(/[?#]/)[0];
    if (typeof to.path === 'string') return to.path;
    return '';
  }

  function isToCurrentRoute(to) {
    if (!to || !route.path) return false;
    if (typeof to === 'object' && to.name && route.name) return to.name === route.name;
    return toPath(to) === route.path;
  }

  function normalizeCrumb(crumb) {
    if (!crumb) return null;
    const label = crumb.label == null ? '' : String(crumb.label).trim();
    if (!label) return null;
    return {
      ...crumb,
      label,
      current: crumb.current === true || isToCurrentRoute(crumb.to),
    };
  }

  const resolvedBreadcrumbs = computed(() => {
    const source = props.breadcrumbs !== undefined ? props.breadcrumbs : route.meta?.breadcrumbs;
    if (source === false) return [];
    const items = typeof source === 'function' ? source(route, t) : source;
    return Array.isArray(items) ? items.map(normalizeCrumb).filter(Boolean) : [];
  });
</script>
